import React from 'react'
import { type Control } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import * as R from 'remeda'
import { match } from 'ts-pattern'

import { Typography } from '~/components/elements/Typography'
import {
  ConditionOperationSelector,
  type ConditionOperationSelectorProps,
} from '~/components/features/ConditionOperationSelector'
import {
  CustomerAttributeGroupSelector,
  type CustomerAttributeGroupSelectorProps,
} from '~/components/features/CustomerAttributeGroupSelector'
import { SelectorListContainer } from '~/components/features/SelectorListContainer'
import { useFormField } from '~/hooks/useFormField'
import {
  compact,
  ensureExists,
  equalsByPath,
  findByPath,
  getByPath,
  pathAssume,
} from '~/utils'

import { type CustomerAttributeGroup, type InputValues } from '../utils'

import { CustomerAttributesInput } from './CustomerAttributesInput'

export type CustomerAttributeGroupInputProps = {
  control: Control<InputValues>
  customerAttributeGroups: CustomerAttributeGroup[]
  filterGroupIndex: number
  filterIndex: number
}

export const CustomerAttributeGroupInput = ({
  control,
  customerAttributeGroups,
  filterGroupIndex,
  filterIndex,
}: CustomerAttributeGroupInputProps) => {
  const { t } = useTranslation()
  const namePrefix =
    `filterGroups.${filterGroupIndex}.${filterIndex}.customerAttribute` as const
  const operationField = useFormField(control, `${namePrefix}.operation`)
  const customerAttributeIdsField = useFormField(
    control,
    `${namePrefix}.customerAttributeIds`,
  )

  const [customerAttributeGroupId, setCustomerAttributeGroupId] =
    React.useState(
      R.pipe(
        customerAttributeGroups.find(({ customerAttributes }) =>
          customerAttributes.some(
            equalsByPath('id', customerAttributeIdsField.value?.[0]),
          ),
        ),
        pathAssume('id'),
      ),
    )
  const customerAttributeGroup = React.useMemo(
    () =>
      R.pipe(
        customerAttributeGroups,
        findByPath('id', customerAttributeGroupId),
        ensureExists,
      ),
    [customerAttributeGroups, customerAttributeGroupId],
  )

  const getDefaultCustomerAttributeIds = React.useCallback(
    (customerAttributeGroupId: CustomerAttributeGroup['id']) => {
      const customerAttributeGroup = findByPath(
        customerAttributeGroups,
        'id',
        customerAttributeGroupId,
      )
      const customerAttribute = getByPath(
        customerAttributeGroup,
        'customerAttributes.0',
      )

      return compact([customerAttribute?.id])
    },
    [customerAttributeGroups],
  )

  const handleCustomerAttributeGroupChange = React.useCallback<
    EH<CustomerAttributeGroupSelectorProps<CustomerAttributeGroup>, 'onChange'>
  >(
    (customerAttributeGroupId) => {
      setCustomerAttributeGroupId(customerAttributeGroupId)

      const customerAttributeGroupIds = match(operationField.value)
        .with('equal', 'not_equal', () =>
          getDefaultCustomerAttributeIds(customerAttributeGroupId),
        )
        .with('in', 'not_in', () => [])
        .exhaustive()
      customerAttributeIdsField.onChange(customerAttributeGroupIds)
    },
    [
      customerAttributeIdsField,
      getDefaultCustomerAttributeIds,
      operationField.value,
    ],
  )

  const handleOperationChange = React.useCallback<
    EH<ConditionOperationSelectorProps, 'onChange'>
  >(
    (operation) => {
      const prevOperation = operationField.value
      const prevCustomerAttributeIds = customerAttributeIdsField.value

      operationField.onChange(operation)

      if (
        ['in', 'not_in'].includes(prevOperation) &&
        ['equal', 'not_equal'].includes(operation) &&
        prevCustomerAttributeIds.length !== 1
      ) {
        const customerAttributeIds = match(prevCustomerAttributeIds.length)
          .with(0, () =>
            getDefaultCustomerAttributeIds(customerAttributeGroupId),
          )
          .otherwise(() => prevCustomerAttributeIds.slice(0, 1))
        customerAttributeIdsField.onChange(customerAttributeIds)
      }
    },
    [
      customerAttributeGroupId,
      customerAttributeIdsField,
      getDefaultCustomerAttributeIds,
      operationField,
    ],
  )

  return (
    <SelectorListContainer>
      <Typography fontWeight={700} variant="heading3">
        {t('customized_segments_group_3')}
      </Typography>

      <CustomerAttributeGroupSelector
        customerAttributeGroups={customerAttributeGroups}
        value={customerAttributeGroup.id}
        onChange={handleCustomerAttributeGroupChange}
      />

      <Typography fontWeight={700} variant="heading3">
        {t('condition')}
      </Typography>

      <ConditionOperationSelector
        {...operationField}
        onChange={handleOperationChange}
      />

      <Typography fontWeight={700} variant="heading3">
        {t('attribute')}
      </Typography>

      <CustomerAttributesInput
        conditionOperation={operationField.value}
        customerAttributes={customerAttributeGroup.customerAttributes}
        {...customerAttributeIdsField}
      />
    </SelectorListContainer>
  )
}
